import React, { Fragment } from "react";

import img13 from "../../../images/service/webapp/web development.png";
import img14 from "../../../images/service/webapp/web development (2).png";
import img15 from "../../../images/service/webapp/web development (3).png";


// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Custom Websites & Brand Digital Presence",
    content:
      "Stunning brand websites, corporate platforms, and landing pages designed to convert visitors into customers with exceptional design and performance.",
  },
  {
    icon: sIcon2,
    title: "E-commerce & Online Stores",
    content:
      "Powerful online stores with seamless checkout experiences, inventory management, payment integration, and conversion-optimized user flows.",
  },
  {
    icon: sIcon3,
    title: "Web Applications & SaaS Platforms",
    content:
      "Complex web applications, SaaS products, and enterprise platforms built with scalable architecture and robust backend systems.",
  },
  {
    icon: sIcon4,
    title: "Progressive Web Apps",
    content:
      "Fast, app-like web experiences that work offline, load instantly, and provide native-app functionality through modern web technologies.",
  },
];

const benefits = [
  {
    title: "+68% Conversion Rate",
    description:
      "Average conversion rate increase through strategic design and optimized user flows that guide visitors toward action.",
    icon: sIcon1,
  },
  {
    title: "94+ PageSpeed Score",
    description:
      "Lightning-fast load times optimized for Core Web Vitals, ensuring superior user experience and SEO performance.",
    icon: sIcon2,
  },
  {
    title: "2–5× Traffic Growth",
    description:
      "Organic traffic growth within the first year through technical SEO best practices built from day one.",
    icon: sIcon3,
  },
  {
    title: "Scalable Architecture",
    description:
      "Modern tech stack and clean code that grows with your business and adapts to evolving market demands.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What technologies do you use for web development?",
    a: "We leverage modern frameworks like React, Next.js, and Vue.js combined with robust backend technologies to create high-performance, scalable web applications optimized for speed and user experience.",
  },
  {
    q: "How long does it take to build a custom website?",
    a: "Timelines vary based on complexity. A standard business website takes 4-6 weeks, while complex e-commerce or web applications typically require 8-16 weeks from discovery to launch.",
  },
  {
    q: "Will my website be mobile-friendly and SEO-optimized?",
    a: "Absolutely. We follow mobile-first design principles and implement technical SEO best practices from day one, ensuring your site ranks well and provides excellent experiences across all devices.",
  },
  {
    q: "Do you provide ongoing support after launch?",
    a: "Yes, we offer comprehensive post-launch support including hosting management, security updates, performance monitoring, and ongoing optimization to ensure your website continues to deliver results.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function WebDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Web Development"
        pagesub="Blazing-fast, conversion-optimized websites that drive growth"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            <span className="gradient-text">Web Development</span>{" "}
            That Ignites Growth
          </>
        }
        subtitle={`We craft blazing-fast, conversion-optimized, visually stunning websites and web 
applications using cutting-edge technologies — designed to load instantly, rank high, 
convert visitors and scale with your business.`}
        buttonText="Start Your Project"
        buttonLink="/contact"
        image={img13}
        imageAlt="Modern Web Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Modern Web Development Matters"
        image={img14}
        paragraphs={[
          "In today's digital-first world, your website is often the first impression customers have of your brand. Users expect websites to load in under 2 seconds and provide seamless experiences across all devices.",
          "Search engines reward fast, well-structured sites with higher rankings. Our development approach ensures your website excels in performance, accessibility, and conversion optimization — driving real business growth through strategic technology choices and user-centered design.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Our Web Development Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Our Web Creation Journey"
        image={img15}
        paragraphs={[
          "Discovery & Strategy: We begin with business goals, user personas, competitor benchmarking, and feature roadmap to define the perfect tech stack and SEO strategy tailored to your needs.",
          "Design & Development: From wireframes to high-fidelity UI, we create mobile-first layouts with clean code architecture. Our frontend expertise (React/Next.js/Vue) combined with robust backend systems ensures blazing-fast performance optimized for Core Web Vitals.",
          "Launch & Growth: Multi-stage testing, SEO optimization, analytics integration, and post-launch monitoring ensure your website launches strong and continues to improve through speed and conversion iteration.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Real Results from Our Web Work"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our Web Solutions"
        subtitle="Performance, design, and technology excellence built into every project"
        features={[
          {
            icon: sIcon1,
            title: "Blazing-Fast Performance",
            description:
              "Optimized for Core Web Vitals with 94+ PageSpeed scores, ensuring lightning-fast load times and superior user experience.",
          },
          {
            icon: sIcon2,
            title: "Mobile-First Responsive Design",
            description:
              "Seamless experiences across all devices with layouts that adapt perfectly to any screen size.",
          },
          {
            icon: sIcon3,
            title: "SEO & Search Visibility",
            description:
              "Built with technical SEO best practices from day one, achieving 2-5× organic traffic growth.",
          },
          {
            icon: sIcon4,
            title: "Conversion-Focused Design",
            description:
              "Every element strategically designed to guide users toward action, resulting in 68% conversion rate increases.",
          },
          {
            icon: sIcon5,
            title: "Scalable & Future-Ready",
            description:
              "Modern tech stack and clean architecture that grows with your business and adapts to market demands.",
          },
          {
            icon: sIcon6,
            title: "Security & Accessibility",
            description:
              "WCAG accessibility standards and robust security implementation protecting your users and data.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "High-performance modern frameworks (React, Next.js, Vue.js)",
          "Mobile-first responsive design with pixel-perfect execution",
          "Technical SEO expertise driving 2-5× organic traffic growth",
          "Comprehensive post-launch support and continuous optimization",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
